/**
 * Tabs, driven by a value the caller owns.
 *
 * Uncontrolled tabs are the usual mistake: the selected tab belongs in the URL
 * so that a link to "the tariff conditions of this customer" is a link, and so
 * that the browser's back button undoes a tab change like everything else.
 * This component therefore holds no state.
 *
 * Keyboard behaviour follows the ARIA tabs pattern — arrows move between tabs,
 * Home and End jump to the ends — because a tab strip that only responds to
 * Tab makes a keyboard user walk through every panel to reach the last tab.
 */

import { useRef, type KeyboardEvent } from "react";

import "./layout.css";

export interface TabItem {
  id: string;
  label: string;
  /** A count beside the label. */
  count?: number;
}

export interface TabsProps {
  items: readonly TabItem[];
  active: string;
  onSelect: (id: string) => void;
  /** Announced as the tab list's name. */
  label: string;
}

export function Tabs({ items, active, onSelect, label }: TabsProps) {
  const strip = useRef<HTMLDivElement>(null);

  // On each tab rather than on the strip. The strip is deliberately not
  // focusable — the ARIA pattern puts focus on the tabs and lets the event
  // bubble — but a handler on a non-focusable interactive role is
  // indistinguishable from a mistake, both to a linter and to a reader. On the
  // buttons it depends on nothing.
  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    const index = items.findIndex((item) => item.id === active);
    if (index < 0) return;

    const moves: Record<string, number | undefined> = {
      ArrowRight: index + 1,
      ArrowLeft: index - 1,
      Home: 0,
      End: items.length - 1,
    };
    const target = moves[event.key];
    if (target === undefined) return;

    event.preventDefault();
    // Wraps, which is what the pattern specifies and what stops a user
    // pressing right repeatedly and wondering why nothing happens.
    const next = items.at((target + items.length) % items.length);
    if (next === undefined) return;
    onSelect(next.id);
    strip.current?.querySelector<HTMLButtonElement>(`[data-tab="${next.id}"]`)?.focus();
  }

  return (
    <div ref={strip} className="hardauto-tabs" role="tablist" aria-label={label}>
      {items.map((item) => {
        const selected = item.id === active;
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            data-tab={item.id}
            className="hardauto-tab"
            aria-selected={selected}
            // Only the selected tab is in the tab order; the arrows move
            // between them. Otherwise Tab walks the strip instead of taking
            // the user into the panel they just chose.
            tabIndex={selected ? 0 : -1}
            onClick={() => onSelect(item.id)}
            onKeyDown={onKeyDown}
          >
            {item.label}
            {item.count === undefined ? null : (
              <span className="hardauto-tab__count">{item.count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
